import React, { useEffect, useMemo, useRef } from 'react';
import { useStore } from 'effector-react';
import { ImageType } from '@types';
import Tip from '@components/Tip';
import {
  $GateRotateAudioLength,
  $GateRotateSubtitleLength,
  $rotateCurrentLeft,
  $rotateEditApi,
  $rotateIsPlaying,
} from '@stores/autoRotateStores';
import { $loadedScene } from '@stores/loadedStore';
import { tlTime } from '@utils/tlTime';
import { TipWrapper } from '@components/TipWrapper';
import Image from '@components/Image';
import { MainPlayer } from '@stores/krpPlayerStores';
import { MS_PER_TICK, PX_PER_SEC, ROTATE_MAX } from '../constants';
import { rotateToPromise } from '../utils/rotateLookTo';
import { onUserInteractive } from '../../player/actions/onUserInteractive';

export function RotateHeader() {
  const audioLength = useStore($GateRotateAudioLength.state);
  const subTitleLength = useStore($GateRotateSubtitleLength.state);
  const scene = useStore($loadedScene)!;
  const { autoRotate } = scene.config;
  const list = autoRotate?.tourData ?? [];
  const maxTime = useMemo(() => {
    const last = list[list.length - 1];
    const length = last ? last.startAt + last.duration : 0;
    return Math.max(length, audioLength, subTitleLength);
  }, [audioLength, subTitleLength, list]);

  const isPlaying = useStore($rotateIsPlaying);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    handleStop();
  }, [autoRotate, audioLength, subTitleLength]);

  useEffect(() => {
    const unwatch0 = onUserInteractive.watch(() => handleStop());
    const unwatch1 = $rotateEditApi.setCurrentLeft.watch(() => handleStop());
    return () => {
      unwatch0();
      unwatch1();
      handleStop();
      $rotateEditApi.reset();
    };
  }, []);

  const handlePlay = async () => {
    const player = MainPlayer.get();
    if (!player) return;
    const curTime = $rotateCurrentLeft.getState() / PX_PER_SEC;
    const curIndex = list.findIndex(({ startAt }, i) => {
      return (
        startAt <= curTime &&
        curTime < (list[i + 1]?.startAt ?? Number.POSITIVE_INFINITY)
      );
    });
    const item = list[curIndex];
    if (item) {
      let target: { fov: number; hlookat: number; vlookat: number };
      if (curTime > item.startAt + item.duration) {
        target = item;
      } else {
        target = player.tracingLookTo({
          startHLookAt: list[curIndex - 1]?.hlookat ?? scene.view.hLookAt,
          startVLookAt: list[curIndex - 1]?.vlookat ?? scene.view.vLookAt,
          startFov: list[curIndex - 1]?.fov ?? scene.view.fov,
          endHLookAt: item.hlookat,
          endVLookAt: item.vlookat,
          endFov: item.fov,
          duration: item.duration,
          currentTime: curTime - item.startAt,
        });
      }
      await rotateToPromise({ ...target, duration: 0.2 });
    }
    $rotateEditApi.play();
    const maxLeft = maxTime * PX_PER_SEC;
    timer.current = setInterval(() => {
      if ($rotateCurrentLeft.getState() < maxLeft) {
        $rotateEditApi.tick();
      } else {
        handleStop();
        $rotateEditApi.setCurrentLeft(0);
      }
    }, MS_PER_TICK);
  };

  const handleStop = () => {
    clearInterval(timer.current!);
    MainPlayer.get()?.stopLookTo();
    $rotateEditApi.stop();
  };

  const toStart = () => {
    handleStop();
    $rotateEditApi.setCurrentLeft(0);
  };
  const toEnd = () => {
    handleStop();
    const last = list[list.length - 1];
    if (!last) return;
    $rotateEditApi.setCurrentLeft((last.startAt + last.duration) * PX_PER_SEC);
  };
  const toPrev = () => {
    handleStop();
    const left = $rotateCurrentLeft.getState() / PX_PER_SEC;
    for (let i = list.length - 1; i >= 0; i--) {
      const item = list[i];
      if (item.startAt < left) {
        $rotateEditApi.setCurrentLeft(item.startAt * PX_PER_SEC);
        return;
      }
    }
    toStart();
  };
  const toNext = () => {
    handleStop();
    const left = $rotateCurrentLeft.getState() / PX_PER_SEC;
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (item.startAt > left) {
        $rotateEditApi.setCurrentLeft(item.startAt * PX_PER_SEC);
        return;
      }
    }
    toEnd();
  };

  return (
    <div className="relative flex h-50 items-center justify-center bg-black-#2d">
      <TipWrapper text="跳转到起始节点" className="mr-15">
        <Image
          src="/imgs/vc-back.678c9dcd270fab9ff2dada2e3260762f.png"
          type={ImageType.sysMedia}
          className="h-16 w-16 cursor-pointer"
          onClick={toStart}
        />
      </TipWrapper>
      <TipWrapper text="上一节点" className="mr-15">
        <Image
          src="/imgs/left.f450193cdda4b78ad1d4092db8fbec28.png"
          type={ImageType.sysMedia}
          className="h-16 w-16 cursor-pointer"
          onClick={toPrev}
        />
      </TipWrapper>
      <TipWrapper text={isPlaying ? '暂停' : '预览'}>
        <Image
          src={
            isPlaying
              ? '/imgs/pause.91c6745617b940649c15d0dda875f108.png'
              : '/imgs/vc-play.e35695e81d4ceda90048d0fc668a28a8.png'
          }
          type={ImageType.sysMedia}
          className="h-24 w-24 cursor-pointer"
          onClick={() => (isPlaying ? handleStop() : handlePlay())}
        />
      </TipWrapper>
      <TipWrapper text="下一节点" className="ml-15">
        <Image
          src="/imgs/right.85a8dd5960c33fb1f03afecddc8fef04.png"
          type={ImageType.sysMedia}
          className="h-16 w-16 cursor-pointer"
          onClick={toNext}
        />
      </TipWrapper>
      <TipWrapper text="跳转到最后一个节点" className="ml-15">
        <Image
          src="/imgs/vc-next.1070e04df2948224bfcda969b8f206b7.png"
          type={ImageType.sysMedia}
          className="h-16 w-16 cursor-pointer"
          onClick={toEnd}
        />
      </TipWrapper>
      <div className="absolute left-20">{`总时长：${tlTime(maxTime)}`}</div>
      <Tip className="absolute right-20">{`节点总数不能超过${ROTATE_MAX}个`}</Tip>
    </div>
  );
}
